import { useListDeletion } from "@/components/settings/lists/useListDeletion";
import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import {
  Plus,
  Trash2,
  ChevronRight,
  Calendar,
  Bell,
  BellOff,
  Copy,
  RefreshCw,
  Check,
  Loader2,
  CloudUpload,
  X,
  LogOut,
  Globe,
  LayoutGrid as Lists,
  SlidersHorizontal,
} from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import {
  TASK_HISTORY_DEFAULT_DAYS,
  TASK_HISTORY_MAX_DAYS,
  TASK_HISTORY_MIN_DAYS,
} from "@shared/taskHistory";
import {
  DURATION_DISPLAY_FORMAT_KEY,
  readDurationDisplayFormat,
  readTimeDisplayFormat,
  TIME_DISPLAY_FORMAT_KEY,
  type TimeDisplayFormat,
} from "@/lib/displayFormat";
import {
  COLOR_KEYS,
  DEFAULT_AREA_COLOR,
  AREA_COLORS,
  ListColorKey,
  getListColor,
  resolvePairedColorKeys,
} from "@/lib/listColors";
import {
  BackgroundColorPicker,
  BarColorOverridePicker,
  ColorPreviewCard,
} from "@/components/foundations/inputs/ColorPicker/ColorPicker";
import { toast } from "sonner";
import {
  notificationPermission,
  requestNotificationPermission,
} from "@/hooks/useTaskAlerts";
import { useAuth } from "@/_core/hooks/useAuth";
import SortableListsManager, {
  type SortableListsEntry,
} from "@/components/foundations/lists/SortableListsManager/SortableListsManager";
import StylizedIcon from "@/components/foundations/style/StylizedIcon/StylizedIcon";

type AreaWithCount = {
  id: number;
  name: string;
  categoryId: number | null;
  categoryName?: string | null;
  categoryColor?: string | null;
  description: string | null;
  color: string;
  bgColor?: string | null;
  kind: "inbox" | "custom";
  priorityNumber: number;
  isGlobalActive: boolean;
  taskCount: number;
  patternCount: number;
};

type Category = {
  id: number;
  name: string;
  description?: string | null;
  color: string;
  bgColor?: string | null;
  sortOrder: number;
  isCollapsed: boolean;
};

const categoryEntryId = (id: number) => `category:${id}`;
const listEntryId = (id: number) => `list:${id}`;

function entryNumericId(id: string) {
  return Number(id.slice(id.indexOf(":") + 1));
}

function buildListsLayout(
  lists: AreaWithCount[],
  categories: Category[]
): SortableListsEntry[] {
  return [
    ...categories.map(category => ({
      order: category.sortOrder,
      typeRank: 0,
      entry: {
        id: categoryEntryId(category.id),
        type: "category" as const,
        label: category.name,
        collapsed: category.isCollapsed,
      },
    })),
    ...lists.map(list => {
      const resolved = resolvePairedColorKeys({
        primaryColor: list.color,
        secondaryColor: list.bgColor,
      });
      const primary = getListColor(resolved.primary);
      const secondary = getListColor(resolved.secondary);
      return {
        order: list.kind === "inbox" ? -1 : list.priorityNumber,
        typeRank: 1,
        entry: {
          id: listEntryId(list.id),
          type: "list" as const,
          label: list.name,
          categoryId: list.categoryId ? categoryEntryId(list.categoryId) : null,
          active: list.isGlobalActive,
          protectedItem: list.kind === "inbox",
          taskCount: list.taskCount,
          patternCount: list.patternCount,
          sortIndicatorColors: {
            background: secondary.soft,
            foreground: primary.ink,
          },
        },
      };
    }),
  ]
    .sort(
      (left, right) =>
        left.order - right.order ||
        left.typeRank - right.typeRank ||
        left.entry.id.localeCompare(right.entry.id)
    )
    .map(item => item.entry);
}

function ColorSwatch({
  selected,
  colorKey,
  onClick,
  size = "md",
}: {
  selected: boolean;
  colorKey: ListColorKey;
  onClick?: () => void;
  size?: "sm" | "md";
}) {
  const c = AREA_COLORS[colorKey];
  const dim = size === "sm" ? "h-6 w-6" : "h-8 w-8";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={c.name}
      className={`${dim} rounded-full transition-all ring-offset-2 ring-offset-background ${
        selected ? "ring-2 ring-foreground/70 scale-105" : "hover:scale-105"
      }`}
      style={{ background: c.base }}
    />
  );
}

type SettingsButtonProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
};

function SettingsButton({
  icon,
  title,
  description,
  onClick,
}: SettingsButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-xl border border-border/60 bg-card px-4 py-4 text-left transition-colors hover:border-border hover:bg-accent/30"
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-lg leading-tight">
          {title}
        </span>
        <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">
          {description}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

export default function Settings() {
  const [, setLocation] = useLocation();

  return (
    <AppShell title="Settings">
      <div className="container max-w-3xl py-8 space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-3xl tracking-tight">Settings</h1>
          <p className="text-muted-foreground text-sm">
            Manage how Daily Groove is organized, connected, and synced.
          </p>
        </header>

        <div className="space-y-3">
          <SettingsButton
            icon={<Lists className="h-5 w-5" />}
            title="Lists"
            description="Create categories, lists, colors, and patterns."
            onClick={() => setLocation("/settings/lists")}
          />
          <SettingsButton
            icon={<Bell className="h-5 w-5" />}
            title="Alerts"
            description="Enable browser notifications for task reminders on this device."
            onClick={() => setLocation("/settings/alerts")}
          />
          <SettingsButton
            icon={<Calendar className="h-5 w-5" />}
            title="Calendar"
            description="Subscribe to your Daily Groove calendar and control calendar sync settings."
            onClick={() => setLocation("/settings/calendar")}
          />
          <SettingsButton
            icon={<SlidersHorizontal className="h-5 w-5" />}
            title="Interface"
            description="Customize how Daily Groove displays and handles information."
            onClick={() => setLocation("/settings/interface")}
          />
          <SettingsButton
            icon={<LogOut className="h-5 w-5" />}
            title="Account"
            description="Review your signed-in account, log out, and manually sync deployments."
            onClick={() => setLocation("/settings/account")}
          />
        </div>

        <BuildInfo />
      </div>
    </AppShell>
  );
}

export function SettingsLists() {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const listsQuery = trpc.lists.list.useQuery();
  const categoriesQuery = trpc.categories.list.useQuery();

  const [createOpen, setCreateOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [name, setName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [categoryColor, setCategoryColor] =
    useState<ListColorKey>(DEFAULT_AREA_COLOR);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("none");
  const [description, setDescription] = useState("");
  // Background is the PRIMARY color; bar is the optional override.
  const [bg, setBg] = useState<ListColorKey>(DEFAULT_AREA_COLOR);
  const [bgAuto, setBgAuto] = useState(true);
  const [barOverride, setBarOverride] = useState<ListColorKey | null>(null);

  const [listLayout, setListLayout] = useState<SortableListsEntry[]>([]);
  const customLists = useMemo(
    () => (listsQuery.data ?? []).filter(l => l.kind === "custom"),
    [listsQuery.data]
  );
  const categories = (categoriesQuery.data ?? []) as Category[];
  useEffect(() => {
    if (listsQuery.data && categoriesQuery.data) {
      setListLayout(
        buildListsLayout(
          listsQuery.data as AreaWithCount[],
          categoriesQuery.data as Category[]
        )
      );
    }
  }, [listsQuery.data, categoriesQuery.data]);

  const saveLayoutMutation = trpc.lists.saveLayout.useMutation();
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const deletion = useListDeletion();
  const createColors = resolvePairedColorKeys({
    primaryColor: barOverride,
    secondaryColor: bgAuto ? null : bg,
  });
  const createMutation = trpc.lists.create.useMutation({
    onSuccess: ({ id }) => {
      utils.lists.list.invalidate();
      utils.categories.list.invalidate();
      setCreateOpen(false);
      setName("");
      setSelectedCategoryId("none");
      setDescription("");
      setBg(DEFAULT_AREA_COLOR);
      setBgAuto(true);
      setBarOverride(null);
      setLocation(`/settings/lists/${id}`);
    },
  });
  const createCategoryMutation = trpc.categories.create.useMutation({
    onSuccess: () => {
      utils.categories.list.invalidate();
      setCategoryOpen(false);
      setCategoryName("");
      setCategoryColor(DEFAULT_AREA_COLOR);
    },
  });

  function handleLayoutChange(next: SortableListsEntry[]) {
    setListLayout(next);
    const entries = next.map(entry =>
      entry.type === "category"
        ? {
            type: "category" as const,
            id: entryNumericId(entry.id),
            collapsed: Boolean(entry.collapsed),
          }
        : {
            type: "list" as const,
            id: entryNumericId(entry.id),
            categoryId: entry.categoryId
              ? entryNumericId(entry.categoryId)
              : null,
            active: entry.active,
          }
    );
    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        await saveLayoutMutation.mutateAsync({ entries });
      })
      .catch(error => {
        console.error("Failed to save list layout", error);
        toast.error("Could not save list changes");
        void Promise.all([
          utils.lists.list.invalidate(),
          utils.categories.list.invalidate(),
        ]);
      });
  }

  return (
    <AppShell backTo="/settings" title="Lists">
      <div className="container max-w-3xl py-8 space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-3xl tracking-tight">Lists</h1>
          <p className="text-muted-foreground text-sm">
            Categories group lists. Lists hold the tasks and patterns you plan
            into days.
          </p>
        </header>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {categories.length}{" "}
              {categories.length === 1 ? "category" : "categories"}
            </span>
            <Dialog open={categoryOpen} onOpenChange={setCategoryOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  New category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">
                    New category
                  </DialogTitle>
                  <DialogDescription>
                    A category is the top-level group for lists, like Work or
                    Home.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="category-name">Name</Label>
                    <Input
                      id="category-name"
                      placeholder="e.g. Work, Home"
                      value={categoryName}
                      onChange={e => setCategoryName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_KEYS.map(key => (
                        <ColorSwatch
                          key={key}
                          colorKey={key}
                          selected={categoryColor === key}
                          onClick={() => setCategoryColor(key)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="ghost"
                    onClick={() => setCategoryOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      !categoryName.trim() || createCategoryMutation.isPending
                    }
                    onClick={() =>
                      createCategoryMutation.mutate({
                        name: categoryName.trim(),
                        color: categoryColor,
                      })
                    }
                  >
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {categoriesQuery.isLoading ? (
            <div className="h-14 rounded-xl bg-muted/40 animate-pulse" />
          ) : categories.length === 0 ? (
            <div className="rounded-xl border border-border/60 bg-card px-3 py-3 text-sm text-muted-foreground">
              No categories yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {categories.map(category => {
                const color = getListColor(category.color);
                const secondaryColor = getListColor(
                  category.bgColor ?? category.color
                );
                const count = customLists.filter(
                  list => list.categoryId === category.id
                ).length;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() =>
                      setLocation(`/settings/categories/${category.id}`)
                    }
                    className="flex w-full items-center gap-3 rounded-xl border border-border/60 bg-card px-3 py-3 text-left transition-colors hover:border-border hover:bg-accent/30"
                  >
                    <StylizedIcon
                      primaryColor={color.base}
                      secondaryColor={secondaryColor.soft}
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-display">
                        {category.name}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {category.description ||
                          `${count} ${count === 1 ? "list" : "lists"}`}
                      </span>
                    </span>
                    <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {customLists.length} custom{" "}
              {customLists.length === 1 ? "list" : "lists"}
            </span>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  New list
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">
                    New list
                  </DialogTitle>
                  <DialogDescription>
                    A list groups a set of tasks and patterns that you can add
                    to any day.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g. Computer, Admin, Nutrition"
                      value={name}
                      onChange={e => setName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={selectedCategoryId}
                      onValueChange={setSelectedCategoryId}
                    >
                      <SelectTrigger id="category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No category</SelectItem>
                        {categories.map(category => (
                          <SelectItem
                            key={category.id}
                            value={String(category.id)}
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="desc">Description</Label>
                      {description && (
                        <button
                          type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => setDescription("")}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                          aria-label="Clear description"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <Textarea
                      id="desc"
                      placeholder="What this list is for"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <BarColorOverridePicker
                    value={barOverride}
                    onChange={k => {
                      setBarOverride(k);
                    }}
                    bgColor={createColors.secondary}
                    label="Primary color"
                  />
                  <BackgroundColorPicker
                    value={bgAuto ? null : bg}
                    onChange={k => {
                      if (k === null) {
                        setBgAuto(true);
                        setBg(barOverride ?? bg);
                      } else {
                        setBgAuto(false);
                        setBg(k);
                      }
                    }}
                    label="Secondary color"
                    showAuto
                    autoColor={createColors.primary}
                  />
                  <div className="space-y-1.5 pt-1">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      Preview
                    </p>
                    <ColorPreviewCard
                      barColor={barOverride}
                      bgColor={bgAuto ? null : bg}
                      title={name.trim() || "Sample task"}
                      label={name.trim() || "List"}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!name.trim() || createMutation.isPending}
                    onClick={() =>
                      createMutation.mutate({
                        name: name.trim(),
                        categoryId:
                          selectedCategoryId === "none"
                            ? null
                            : Number(selectedCategoryId),
                        description: description.trim() || undefined,
                        color:
                          selectedCategoryId === "none"
                            ? createColors.primary
                            : null,
                        bgColor:
                          selectedCategoryId === "none" && !bgAuto ? bg : null,
                      })
                    }
                  >
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {listsQuery.isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="h-16 rounded-xl bg-muted/40 animate-pulse"
                />
              ))}
            </div>
          ) : (listsQuery.data?.length ?? 0) === 0 ? (
            <div className="h-16 rounded-xl bg-muted/40 animate-pulse" />
          ) : (
            <SortableListsManager
              initialLayout={listLayout}
              layout={listLayout}
              onLayoutChange={handleLayoutChange}
              onOpenList={list =>
                setLocation(`/settings/lists/${entryNumericId(list.id)}`)
              }
              onDeleteList={list =>
                void deletion.remove(
                  "list",
                  entryNumericId(list.id),
                  list.label
                )
              }
              onDeleteCategory={category =>
                void deletion.remove(
                  "category",
                  entryNumericId(category.id),
                  category.label
                )
              }
            />
          )}

          <div className="border-t border-border/60 pt-6">
            <p className="text-xs text-muted-foreground">
              Inbox is your default list and stays at the top. Disabled custom
              lists are hidden from the day-planning flow but their data is
              preserved.
            </p>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

export function SettingsAlerts() {
  return (
    <AppShell backTo="/settings" title="Alerts">
      <div className="container max-w-3xl py-8">
        <NotificationAlertsSection standalone />
      </div>
    </AppShell>
  );
}

export function SettingsCalendar() {
  return (
    <AppShell backTo="/settings" title="Calendar">
      <div className="container max-w-3xl py-8">
        <CalendarSubscriptionSection standalone />
      </div>
    </AppShell>
  );
}

export function SettingsInterface() {
  const utils = trpc.useUtils();
  const preference = trpc.preferences.timePickerIncrement.useQuery();
  const updatePreference = trpc.preferences.setTimePickerIncrement.useMutation({
    onSuccess: () => utils.preferences.timePickerIncrement.invalidate(),
  });
  const rememberLastDay = trpc.preferences.rememberLastDay.useQuery();
  const updateRememberLastDay = trpc.preferences.setRememberLastDay.useMutation(
    {
      onError: () => utils.preferences.rememberLastDay.invalidate(),
    }
  );
  const taskHistoryPreference = trpc.preferences.taskHistory.useQuery();
  const updateTaskHistoryPreference =
    trpc.preferences.setTaskHistory.useMutation({
      onError: () => utils.preferences.taskHistory.invalidate(),
      onSuccess: () => {
        utils.preferences.taskHistory.invalidate();
        utils.tasks.listInbox.invalidate();
        utils.tasks.listByList.invalidate();
        utils.lists.overview.invalidate();
      },
    });
  function setRememberLastDay(surface: "home" | "calendar", enabled: boolean) {
    utils.preferences.rememberLastDay.setData(undefined, current => ({
      home: surface === "home" ? enabled : (current?.home ?? true),
      calendar: surface === "calendar" ? enabled : (current?.calendar ?? true),
    }));
    updateRememberLastDay.mutate({ surface, enabled });
  }
  const increment = preference.data?.increment ?? 1;
  const taskHistory = taskHistoryPreference.data ?? {
    enabled: false,
    days: TASK_HISTORY_DEFAULT_DAYS,
  };
  const [taskHistoryDays, setTaskHistoryDays] = useState(
    String(TASK_HISTORY_DEFAULT_DAYS)
  );
  useEffect(() => {
    if (taskHistoryPreference.data)
      setTaskHistoryDays(String(taskHistoryPreference.data.days));
  }, [taskHistoryPreference.data?.days]);
  function updateTaskHistory(patch: Partial<typeof taskHistory>) {
    const next = { ...taskHistory, ...patch };
    utils.preferences.taskHistory.setData(undefined, next);
    updateTaskHistoryPreference.mutate(next);
  }
  function saveTaskHistoryDays() {
    const parsed = Number(taskHistoryDays);
    const days =
      Number.isInteger(parsed) &&
      parsed >= TASK_HISTORY_MIN_DAYS &&
      parsed <= TASK_HISTORY_MAX_DAYS
        ? parsed
        : taskHistory.days;
    setTaskHistoryDays(String(days));
    if (days !== taskHistory.days) updateTaskHistory({ days });
  }
  const [timeDisplayFormat, setTimeDisplayFormat] = useState<TimeDisplayFormat>(
    readTimeDisplayFormat
  );
  const [durationDisplayFormat, setDurationDisplayFormat] =
    useState<TimeDisplayFormat>(readDurationDisplayFormat);

  return (
    <AppShell backTo="/settings" title="Interface">
      <div className="container max-w-3xl py-8 space-y-6">
        <header className="space-y-2">
          <h1 className="font-display text-3xl tracking-tight">Interface</h1>
          <p className="text-muted-foreground text-sm">
            Customize how Daily Groove displays and handles information.
          </p>
        </header>
        <div className="rounded-xl border border-border/60 bg-card p-4 space-y-4">
          <div>
            <Label>Remember last day</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Return to the last selected day instead of starting on today.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="remember-home-day" className="font-normal">
              Homepage
            </Label>
            <Switch
              id="remember-home-day"
              checked={rememberLastDay.data?.home ?? true}
              onCheckedChange={enabled => setRememberLastDay("home", enabled)}
              disabled={updateRememberLastDay.isPending}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="remember-calendar-day" className="font-normal">
              Calendar
            </Label>
            <Switch
              id="remember-calendar-day"
              checked={rememberLastDay.data?.calendar ?? true}
              onCheckedChange={enabled =>
                setRememberLastDay("calendar", enabled)
              }
              disabled={updateRememberLastDay.isPending}
            />
          </div>
        </div>
        <div className="rounded-xl border border-border/60 bg-card p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label htmlFor="task-history-enabled">Task history</Label>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Keep daily views focused by omitting older tasks from homepage,
                Inbox, and list lists. This never deletes task history.
              </p>
            </div>
            <Switch
              id="task-history-enabled"
              checked={taskHistory.enabled}
              onCheckedChange={enabled => updateTaskHistory({ enabled })}
              disabled={updateTaskHistoryPreference.isPending}
            />
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-border/50 pt-4">
            <Label htmlFor="task-history-days" className="font-normal">
              Hide tasks older than
            </Label>
            <div className="flex shrink-0 items-center gap-2">
              <Input
                id="task-history-days"
                type="number"
                min={TASK_HISTORY_MIN_DAYS}
                max={TASK_HISTORY_MAX_DAYS}
                value={taskHistoryDays}
                onChange={event => setTaskHistoryDays(event.target.value)}
                onBlur={saveTaskHistoryDays}
                onKeyDown={event => {
                  if (event.key === "Enter") event.currentTarget.blur();
                }}
                className="h-9 w-20 text-right"
                aria-label="Days before older tasks are hidden"
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Turn this off at any time to restore all tasks to everyday views.
            Future reports and All history search will continue to include every
            retained task.
          </p>
        </div>
        <div className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
          <Label htmlFor="time-picker-increment">Minute increments</Label>
          <Select
            value={String(increment)}
            onValueChange={value =>
              updatePreference.mutate({ increment: Number(value) as 1 | 5 })
            }
          >
            <SelectTrigger id="time-picker-increment" className="max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 minutes</SelectItem>
              <SelectItem value="1">1 minute</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
          <Label htmlFor="duration-display-format">
            Header duration format
          </Label>
          <Select
            value={durationDisplayFormat}
            onValueChange={value => {
              const next = value as TimeDisplayFormat;
              setDurationDisplayFormat(next);
              window.localStorage.setItem(DURATION_DISPLAY_FORMAT_KEY, next);
            }}
          >
            <SelectTrigger id="duration-display-format" className="max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="compact">Compact (135m)</SelectItem>
              <SelectItem value="alternate">Alternate (2h 15m)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
          <Label htmlFor="time-display-format">Header time format</Label>
          <Select
            value={timeDisplayFormat}
            onValueChange={value => {
              const next = value as TimeDisplayFormat;
              setTimeDisplayFormat(next);
              window.localStorage.setItem(TIME_DISPLAY_FORMAT_KEY, next);
            }}
          >
            <SelectTrigger id="time-display-format" className="max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="compact">Compact (14:00, 135m)</SelectItem>
              <SelectItem value="alternate">
                Alternate (2:00 PM, 2h 15m)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </AppShell>
  );
}

export function SettingsAccount() {
  return (
    <AppShell backTo="/settings" title="Account">
      <div className="container max-w-3xl py-8 space-y-8">
        <AccountSection standalone />
        <BuildInfo />
      </div>
    </AppShell>
  );
}

function BuildInfo() {
  return (
    <div className="border-t border-border/60 pt-6">
      <p className="text-xs text-muted-foreground">
        {__APP_ENV__} · {__APP_VERSION__}
        {__APP_VERSION_TITLE__ ? ` ${__APP_VERSION_TITLE__}` : ""} · Build{" "}
        {__BUILD_SHA__} · {new Date(__BUILD_TIME__).toLocaleString()}
      </p>
    </div>
  );
}

function AccountSection({ standalone = false }: { standalone?: boolean }) {
  const { user, logout, loading } = useAuth();

  async function handleLogout() {
    await logout();
    window.location.href = "/";
  }

  const [isManualSyncing, setIsManualSyncing] = useState(false);

  const triggerManualSync = async () => {
    setIsManualSyncing(true);
    try {
      const response = await fetch("/api/scheduled/auto-deploy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ manual: true }),
      });
      const data = await response.json();
      if (data.ok) {
        if (data.deployed) {
          toast.success(`Deployed! ${data.newCommits} new commit(s) synced.`);
        } else {
          toast.info("No new changes on GitHub.");
        }
      } else {
        toast.error(`Sync failed: ${data.error}`);
      }
    } catch (error) {
      toast.error("Failed to trigger sync.");
    } finally {
      setIsManualSyncing(false);
    }
  };

  return (
    <section
      className={`${standalone ? "" : "border-t border-border/60 pt-8"} space-y-4`}
    >
      <header className="space-y-1.5">
        <div className="flex items-center gap-2">
          <LogOut className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-2xl tracking-tight">Account</h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-prose">
          Sign out of this device.
        </p>
      </header>

      <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-card p-4">
        <div className="min-w-0">
          <div className="font-medium truncate">
            {user?.name || "Signed in"}
          </div>
          {user?.email && (
            <div className="mt-1 text-xs text-muted-foreground truncate">
              {user.email}
            </div>
          )}
        </div>
        <Button
          variant="outline"
          onClick={handleLogout}
          disabled={loading}
          className="shrink-0 gap-2 text-destructive hover:text-destructive"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </Button>
      </div>

      <Button
        id="deploy-button"
        onClick={triggerManualSync}
        disabled={isManualSyncing}
        variant="outline"
        size="lg"
        className="w-full gap-2"
      >
        {isManualSyncing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Syncing from GitHub...
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4" />
            Manual Sync (Deploy Now)
          </>
        )}
      </Button>
    </section>
  );
}

function NotificationAlertsSection({
  standalone = false,
}: {
  standalone?: boolean;
}) {
  const [perm, setPerm] = useState<NotificationPermission | "unsupported">(
    "default"
  );

  useEffect(() => {
    setPerm(notificationPermission());
  }, []);

  async function toggleNotifications() {
    if (perm === "unsupported") {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    if (perm === "denied") {
      toast.message(
        "Notifications are blocked. Enable them in your browser settings."
      );
      return;
    }
    const next = await requestNotificationPermission();
    setPerm(next);
    if (next === "granted") toast.success("Alerts enabled");
  }

  const enabled = perm === "granted";
  const blocked = perm === "denied";
  const unsupported = perm === "unsupported";

  return (
    <section
      className={`${standalone ? "" : "border-t border-border/60 pt-8"} space-y-4`}
    >
      <header className="space-y-1.5">
        <div className="flex items-center gap-2">
          {enabled ? (
            <Bell className="h-4 w-4 text-muted-foreground" />
          ) : (
            <BellOff className="h-4 w-4 text-muted-foreground" />
          )}
          <h2 className="font-display text-2xl tracking-tight">Alerts</h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-prose">
          Enable browser notifications for task reminders.
        </p>
      </header>

      <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-card p-4">
        <div className="min-w-0">
          <div className="font-medium">
            {enabled
              ? "Alerts enabled"
              : blocked
                ? "Alerts blocked"
                : unsupported
                  ? "Alerts unavailable"
                  : "Alerts off"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {blocked
              ? "Enable notifications in your browser settings to receive reminders."
              : unsupported
                ? "This browser does not support notifications."
                : "Task alerts use this browser's notification permission."}
          </div>
        </div>
        <Button
          variant={enabled ? "outline" : "default"}
          onClick={toggleNotifications}
          disabled={enabled || unsupported}
          className="shrink-0 gap-2"
        >
          {enabled ? (
            <Bell className="h-4 w-4" />
          ) : (
            <BellOff className="h-4 w-4" />
          )}
          {enabled ? "Enabled" : "Enable"}
        </Button>
      </div>
    </section>
  );
}

function CalendarSubscriptionSection({
  standalone = false,
}: {
  standalone?: boolean;
}) {
  const tokenQuery = trpc.calendar.getToken.useQuery();
  const utils = trpc.useUtils();
  const regenerate = trpc.calendar.regenerate.useMutation({
    onSuccess: () => {
      utils.calendar.getToken.invalidate();
      toast.success("New subscription URL generated");
    },
  });

  const autoSyncQuery = trpc.calendar.getAutoSync.useQuery();
  const setAutoSync = trpc.calendar.setAutoSync.useMutation({
    onSuccess: () => {
      utils.calendar.getAutoSync.invalidate();
      utils.calendar.syncStatus.invalidate();
    },
  });

  const syncStatusQuery = trpc.calendar.syncStatus.useQuery(undefined, {
    refetchOnWindowFocus: true,
  });
  const autoSyncEnabled = autoSyncQuery.data?.autoSync ?? true;
  const [justSyncedAt, setJustSyncedAt] = useState<Date | null>(null);
  const syncNow = trpc.calendar.syncNow.useMutation({
    onSuccess: ({ syncedAt, eventCount }) => {
      setJustSyncedAt(new Date(syncedAt));
      utils.calendar.syncStatus.invalidate();
      toast.success(
        `Synced ${eventCount} ${eventCount === 1 ? "event" : "events"}`
      );
    },
    onError: () => {
      toast.error("Sync failed. Try again.");
    },
  });

  const status = syncStatusQuery.data;
  const pending = status?.pendingChangeCount ?? 0;
  const lastSyncedAt = status?.lastSyncedAt
    ? new Date(status.lastSyncedAt)
    : null;
  const lastChangedAt = status?.lastChangedAt
    ? new Date(status.lastChangedAt)
    : null;
  const isSyncing = syncNow.isPending;
  const isOutdated =
    (lastChangedAt && lastSyncedAt && lastChangedAt > lastSyncedAt) ||
    pending > 0;
  const upToDate = !isOutdated && !!lastSyncedAt && !isSyncing;
  const neverSynced = !lastSyncedAt;

  // Auto-clear the "just synced" indicator after 4s.
  useEffect(() => {
    if (!justSyncedAt) return;
    const t = window.setTimeout(() => setJustSyncedAt(null), 4000);
    return () => window.clearTimeout(t);
  }, [justSyncedAt]);

  const token = tokenQuery.data?.token;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // Timezone
  const timezoneQuery = trpc.calendar.getTimezone.useQuery();
  const setTimezone = trpc.calendar.setTimezone.useMutation({
    onSuccess: () => {
      utils.calendar.getTimezone.invalidate();
      toast.success("Timezone saved");
    },
    onError: () => toast.error("Failed to save timezone"),
  });
  const storedTimezone = timezoneQuery.data?.timezone ?? null;
  // Auto-detect and save on first load if not yet set
  useEffect(() => {
    if (timezoneQuery.isSuccess && !storedTimezone) {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) setTimezone.mutate({ timezone: detected });
    }
  }, [timezoneQuery.isSuccess, storedTimezone]);

  // Common IANA timezone list (covers most users)
  const TIMEZONES = [
    "Pacific/Honolulu",
    "America/Anchorage",
    "America/Los_Angeles",
    "America/Denver",
    "America/Phoenix",
    "America/Chicago",
    "America/New_York",
    "America/Halifax",
    "America/St_Johns",
    "America/Sao_Paulo",
    "Atlantic/Azores",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Helsinki",
    "Europe/Istanbul",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Dhaka",
    "Asia/Bangkok",
    "Asia/Shanghai",
    "Asia/Tokyo",
    "Asia/Seoul",
    "Australia/Perth",
    "Australia/Adelaide",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];
  const [tzSearch, setTzSearch] = useState("");
  const filteredTz = useMemo(() => {
    if (!tzSearch) return TIMEZONES;
    const q = tzSearch.toLowerCase();
    return TIMEZONES.filter(tz => tz.toLowerCase().includes(q));
  }, [tzSearch]);
  const httpsUrl = token ? `${origin}/api/ical/${token}.ics` : "";
  const webcalUrl = httpsUrl.replace(/^https?:/, "webcal:");
  const tasksUrl = token ? `${origin}/api/ical/${token}/tasks.ics` : "";

  function copy(url: string) {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Copied"),
      () => toast.error("Couldn’t copy. Long-press to copy manually.")
    );
  }

  function formatRelative(d: Date | null): string {
    if (!d) return "never";
    const diffMs = Date.now() - d.getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  return (
    <section
      className={`${standalone ? "" : "border-t border-border/60 pt-8"} space-y-5`}
    >
      <header className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-2xl tracking-tight">
            Calendar subscription
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-prose">
          Subscribe with Google, Apple, or Outlook to see your Daily Groove
          tasks on your existing calendar — with built-in reminders and no app
          to keep open. With auto-sync on, your calendar updates automatically
          within a minute of any change.
        </p>
      </header>

      {/* Auto-sync toggle */}
      <div className="bg-card border border-border/60 rounded-xl p-5 flex items-center justify-between">
        <div className="space-y-1">
          <Label className="text-sm font-medium">Auto-sync</Label>
          <p className="text-xs text-muted-foreground">
            {autoSyncEnabled
              ? "Changes sync automatically"
              : "Manual sync required"}
          </p>
        </div>
        <Switch
          checked={autoSyncEnabled}
          onCheckedChange={checked => setAutoSync.mutate({ autoSync: checked })}
          disabled={setAutoSync.isPending}
        />
      </div>

      {/* Last synced status — always visible */}
      <div className="text-xs font-mono text-muted-foreground px-1">
        Last sync: {formatRelative(lastSyncedAt)}
        {lastChangedAt && <> · last edit: {formatRelative(lastChangedAt)}</>}
        {autoSyncEnabled && isSyncing && (
          <span className="ml-2 inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            syncing…
          </span>
        )}
      </div>

      {/* Manual sync card - only show when autoSync is disabled */}
      {!autoSyncEnabled && (
        <div
          className={`border rounded-xl p-5 transition-colors ${
            isSyncing
              ? "bg-card border-border/60"
              : pending > 0
                ? "bg-foreground text-background border-foreground"
                : "bg-card border-border/60"
          }`}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] opacity-70">
                Snapshot
              </div>
              <div className="font-display text-xl mt-1">
                {isSyncing
                  ? "Syncing\u2026"
                  : justSyncedAt
                    ? "Synced just now"
                    : neverSynced
                      ? "Not yet synced"
                      : pending > 0
                        ? `${pending} pending change${pending === 1 ? "" : "s"}`
                        : "Up to date"}
              </div>
            </div>

            <Button
              onClick={() => syncNow.mutate()}
              disabled={isSyncing || (upToDate && !justSyncedAt)}
              variant={pending > 0 && !isSyncing ? "secondary" : "default"}
              size="lg"
              className="gap-2 min-w-[140px] justify-center"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Syncing
                </>
              ) : justSyncedAt ? (
                <>
                  <Check className="h-4 w-4" />
                  Synced
                </>
              ) : pending > 0 || neverSynced ? (
                <>
                  <CloudUpload className="h-4 w-4" />
                  Sync now
                  {pending > 0 && (
                    <span className="font-mono text-xs opacity-80">
                      · {pending}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Up to date
                </>
              )}
            </Button>
          </div>
          <p className="text-[11px] mt-3 opacity-60">
            Calendars subscribed to your URL only see what was last synced. Past
            edits don't require a sync; only future-day changes count.
          </p>
        </div>
      )}

      {/* Timezone selector */}
      <div className="bg-card border border-border/60 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Timezone</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Required for Google Calendar to show events at the correct time.
          Auto-detected from your browser — change if you need a different
          timezone.
        </p>
        <div className="space-y-1.5">
          <input
            type="text"
            placeholder="Search timezones…"
            value={tzSearch}
            onChange={e => setTzSearch(e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <Select
            value={storedTimezone ?? ""}
            onValueChange={tz => {
              if (tz) setTimezone.mutate({ timezone: tz });
            }}
            disabled={setTimezone.isPending}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  timezoneQuery.isLoading ? "Loading…" : "Select timezone"
                }
              />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              {filteredTz.map(tz => (
                <SelectItem key={tz} value={tz}>
                  {tz.replace(/_/g, " ")}
                </SelectItem>
              ))}
              {filteredTz.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No results
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-card border border-border/60 rounded-xl p-5 space-y-5">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            HTTPS subscription URL
          </Label>
          <div className="flex items-center gap-2">
            <Input readOnly value={httpsUrl} className="font-mono text-xs" />
            <Button
              size="icon"
              variant="outline"
              onClick={() => copy(httpsUrl)}
              aria-label="Copy"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Use this URL with Google Calendar (“Add by URL”) or Outlook.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            One-tap (Apple Calendar)
          </Label>
          <div className="flex items-center gap-2">
            <Input readOnly value={httpsUrl} className="font-mono text-xs" />
            <Button
              size="icon"
              variant="outline"
              onClick={() => copy(httpsUrl)}
              aria-label="Copy"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Copy this HTTPS URL for a calendar client or web browser. Use the
            Apple Calendar button below for a direct subscription handoff.
          </p>
        </div>

        <div className="space-y-2 border-t border-border/60 pt-5">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Task subscription URL (experimental)
          </Label>
          <div className="flex items-center gap-2">
            <Input readOnly value={tasksUrl} className="font-mono text-xs" />
            <Button
              size="icon"
              variant="outline"
              onClick={() => copy(tasksUrl)}
              aria-label="Copy task subscription URL"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Exports tasks as VTODO items for clients that support subscribed
            to-do feeds.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!webcalUrl) return;
              window.location.href = webcalUrl;
            }}
            disabled={!webcalUrl}
          >
            Subscribe in Apple Calendar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!httpsUrl) return;
              window.open(
                `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(httpsUrl)}`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
            disabled={!httpsUrl}
          >
            Subscribe in Google Calendar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-muted-foreground"
            onClick={() => {
              if (
                window.confirm(
                  "Generate a new subscription URL? The current URL will stop working."
                )
              ) {
                regenerate.mutate();
              }
            }}
            disabled={regenerate.isPending}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${regenerate.isPending ? "animate-spin" : ""}`}
            />
            Regenerate URL
          </Button>
        </div>
      </div>

      <details className="bg-card/50 border border-border/40 rounded-xl px-5 py-4 text-sm">
        <summary className="cursor-pointer font-display text-base">
          Setup instructions
        </summary>
        <div className="space-y-4 mt-4 text-muted-foreground">
          <div>
            <p className="font-medium text-foreground mb-1">
              Google Calendar (Gmail)
            </p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>
                Click “Subscribe in Google Calendar” above, or open Google
                Calendar on the web.
              </li>
              <li>
                In the left sidebar, next to “Other calendars”, click + and
                choose “From URL”.
              </li>
              <li>
                Paste the HTTPS subscription URL and click “Add calendar”.
              </li>
              <li>
                Open the calendar settings and turn on notifications to be
                reminded.
              </li>
            </ol>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">
              Apple Calendar (iPhone / Mac)
            </p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>
                Tap “Subscribe in Apple Calendar” on your iPhone, or open the
                webcal URL.
              </li>
              <li>
                Confirm the subscription. Default alerts already include
                5-minute reminders.
              </li>
            </ol>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">Outlook</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Open Outlook on the web, go to Calendar.</li>
              <li>Click “Add calendar” → “Subscribe from web”.</li>
              <li>
                Paste the HTTPS subscription URL, name it “Daily Groove”, and
                import.
              </li>
            </ol>
          </div>
          <p className="text-xs">
            Note: subscribed calendars refresh on the calendar app’s schedule
            (often every few hours). For real-time updates, use the in-app
            alerts.
          </p>
        </div>
      </details>
    </section>
  );
}

export { ColorSwatch };
